import React from 'react';
import { Box, Typography, Button, Breadcrumbs, Link, Chip, alpha, Skeleton } from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const PageHeader = ({
  title,
  subtitle,
  breadcrumbs = [],
  actionLabel,
  onAction,
  actionIcon = <Add />,
  actionVariant = 'contained',
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionIcon,
  showBackButton = false,
  backPath,
  badge,
  badgeColor = 'primary',
  loading = false,
  children,
  sx = {},
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mb: 4, ...sx }}>
        {breadcrumbs.length > 0 && <Skeleton variant="text" width={200} height={20} sx={{ mb: 1 }} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={300} height={40} />
            <Skeleton variant="text" width={200} height={24} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton variant="rounded" width={150} height={42} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4, ...sx }}>
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          sx={{
            mb: 2,
            '& .MuiBreadcrumbs-separator': {
              mx: 1,
              color: 'text.disabled',
            },
          }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 500 }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                component={RouterLink}
                to={crumb.path || '#'}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'none',
                  },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {showBackButton && (
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{
                minWidth: 'auto',
                color: 'text.secondary',
                mt: 0.5,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              Volver
            </Button>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              {badge && (
                <Chip
                  label={badge}
                  size="small"
                  color={badgeColor}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {children}
          {secondaryActionLabel && (
            <Button
              variant="outlined"
              startIcon={secondaryActionIcon}
              onClick={onSecondaryAction}
              sx={{
                borderWidth: '1.5px',
                '&:hover': {
                  borderWidth: '1.5px',
                },
              }}
            >
              {secondaryActionLabel}
            </Button>
          )}
          {actionLabel && (
            <Button
              variant={actionVariant}
              startIcon={actionIcon}
              onClick={onAction}
              sx={{
                boxShadow: actionVariant === 'contained' ? 2 : 0,
              }}
            >
              {actionLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PageHeader;
